import { useUnit } from 'effector-react';
import { Col, Modal, Row, Space, Table } from 'antd';
import { Column } from '@ant-design/charts';
import {
  $districtData,
  $districtModal,
  changeOkedsModalsFiltersEv,
  DistrictModalGate,
  resetDistrictModalEv,
} from '../../models/investProjectsModel/index.js';
import { $districtsMap } from '../../models/globalModel/index.js';
import { useState } from 'react';

function InvestDistrictModal() {
  const districtModal = useUnit($districtModal);
  const resetDistrictModal = useUnit(resetDistrictModalEv);
  const districtData = useUnit($districtData);
  const districtsMap = useUnit($districtsMap);

  const setModalFilters = useUnit(changeOkedsModalsFiltersEv);

  const [zoomChart, setZoomChart] = useState({ chart: null, zoom: false });
  const onZoomChange = (newChart) => {
    setZoomChart((prev) => ({
      chart: newChart,
      zoom: prev.chart === newChart ? !prev.zoom : true,
    }));
  };

  const quarterKPIConfig = {
    data: districtData?.quarter_kpi || [],
    isGroup: true,
    xField: 'quarter',
    yField: 'value',
    seriesField: 'type',
    label: {
      position: 'middle',
      layout: [
        {
          type: 'interval-adjust-position',
        },
        {
          type: 'interval-hide-overlap',
        },
        {
          type: 'adjust-color',
        },
      ],
    },
  };

  const projectsColumns = [
    {
      title: 'ID проекта',
      key: 'id',
      dataIndex: 'id',
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: 'Район',
      key: 'district_id',
      dataIndex: 'district_id',
      sorter: (a, b) =>
        districtsMap[a.district_id]?.localeCompare(districtsMap[b.district_id]),
      render: (id) => districtsMap[id] ?? '',
    },
    {
      title: 'Вид деятельности проекта',
      key: 'activity',
      dataIndex: 'activity',
      sorter: (a, b) => a.activity.localeCompare(b.activity),
    },
    {
      title: 'Наименование проекта',
      key: 'name',
      dataIndex: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: 'Наименование инициатора проекта',
      key: 'initiator',
      dataIndex: 'initiator',
      sorter: (a, b) => a.initiator.localeCompare(b.initiator),
    },
    {
      title: 'Стадия проекта',
      key: 'phase',
      dataIndex: 'phase',
      sorter: (a, b) => a.phase.localeCompare(b.phase),
    },
    {
      title: 'Стоимость проекта',
      key: 'budget',
      dataIndex: 'budget',
      sorter: (a, b) => a.budget - b.budget,
    },
    {
      title: 'Численность рабочих мест',
      key: 'job_effect',
      dataIndex: 'job_effect',
      sorter: (a, b) => a.job_effect - b.job_effect,
    },
    {
      title: 'Уплата налогов',
      key: 'tax_effect',
      dataIndex: 'tax_effect',
      sorter: (a, b) => a.tax_effect - b.tax_effect,
    },
  ];

  return (
    <Modal
      zIndex={103}
      open={!!districtModal}
      onCancel={(e) => {
        e.stopPropagation();
        setModalFilters({ district_id: null });
        resetDistrictModal();
      }}
      footer={null}
      width="80vw"
      destroyOnClose
    >
      <DistrictModalGate />
      {districtData && (
        <Row gutter={[16, 16]}>
          <Col span={14} order={zoomChart.zoom ? 2 : 1}>
            <div style={{ textAlign: 'center' }}>
              {districtsMap[districtModal?.id]}
            </div>
            <div
              style={{
                display: 'flex',
                columnGap: 32,
                marginBottom: 32,
              }}
            >
              <SubTable
                title="Количество проектов"
                data={districtData.effects.project_count}
              />
              <SubTable
                title="Сумма инвестиций"
                data={districtData.effects.investment_sum}
              />
            </div>
            <div style={{ display: 'flex', columnGap: 32 }}>
              <SubTable
                title="Численность рабочих мест"
                data={districtData.effects.job_effect}
              />
              <SubTable
                title="Уплата налогов"
                data={districtData.effects.tax_effect}
              />
            </div>
          </Col>
          <Col span={zoomChart.zoom ? 24 : 10} order={zoomChart.zoom ? 1 : 2}>
            <Column
              {...quarterKPIConfig}
              onReady={(plot) => {
                plot.on('plot:click', () => {
                  onZoomChange(1);
                });
              }}
            />
          </Col>
          <Col span={24} order={3}>
            <Table
              columns={projectsColumns}
              pagination={false}
              dataSource={districtData.projects}
              rowClassName="pointer"
              style={{ width: '100%', overflow: 'auto' }}
              onRow={(record) => {
                return {
                  onClick: () =>
                    window.open(
                      `${window.location.origin}/view-project/${record.id}`,
                      '_blank'
                    ),
                };
              }}
            />
          </Col>
        </Row>
      )}
    </Modal>
  );
}

const subTableColumns = [
  {
    title: <span style={{ fontWeight: 400 }}>Прединвест-ая стадия</span>,
    key: 'preinvestment_period',
    dataIndex: 'preinvestment_period',
    render: (val) =>
      val && !Number.isNaN(val)
        ? new Intl.NumberFormat('ru-RU', {
            notation: 'standard',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val / 1000)
        : val,
  },
  {
    title: <span style={{ fontWeight: 400 }}>Инвестици-ая стадия</span>,
    key: 'investment_period',
    dataIndex: 'investment_period',
    render: (val) =>
      val && !Number.isNaN(val)
        ? new Intl.NumberFormat('ru-RU', {
            notation: 'standard',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val / 1000)
        : val,
  },
  {
    title: <span style={{ fontWeight: 400 }}>Эксплуатац-ая стадия</span>,
    key: 'post_investment_period',
    dataIndex: 'post_investment_period',
    render: (val) =>
      val && !Number.isNaN(val)
        ? new Intl.NumberFormat('ru-RU', {
            notation: 'standard',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(val / 1000)
        : val,
  },
];

function SubTable(props) {
  const { data, title } = props;
  return (
    <div>
      <div style={{ textAlign: 'center', fontWeight: 700 }}>{title}</div>
      <Table
        dataSource={data}
        columns={subTableColumns}
        size="small"
        pagination={false}
      />
    </div>
  );
}

export default InvestDistrictModal;
